import createClient from 'openapi-fetch'

import { authMiddleware } from '@/api/authMiddleware'
import { BEAM_ENDPOINT } from '@/constants/api'

/**
 * `POST /v1/nodes/{node_id}/reboot` is missing from `src/api/typeApi.ts`: the
 * backend change is not merged, so `yarn gen:api` against stage cannot see the
 * path yet. The shape below mirrors what openapi-typescript emits, so once the
 * endpoint reaches stage this file goes away and the call moves to the shared
 * `client` in `@/api/client`.
 */
interface RebootPaths {
  '/v1/nodes/{node_id}/reboot': {
    parameters: {
      query?: never
      header?: never
      path?: never
      cookie?: never
    }
    get?: never
    put?: never
    post: {
      parameters: {
        query?: never
        header?: never
        path: {
          node_id: string
        }
        cookie?: never
      }
      requestBody?: never
      responses: {
        /** @description Reboot requested */
        202: {
          headers: {
            [name: string]: unknown
          }
          content?: never
        }
        /** @description The node is unknown, out of reach, or refused the command */
        default: {
          headers: {
            [name: string]: unknown
          }
          content: {
            'application/json': {
              error: string
            }
          }
        }
      }
    }
    delete?: never
    options?: never
    head?: never
    patch?: never
    trace?: never
  }
}

const rebootClient = createClient<RebootPaths>({ baseUrl: BEAM_ENDPOINT })

rebootClient.use(authMiddleware)

export const rebootApi = {
  /**
   * Asks the provider agent to reboot the node. A `202` means the agent took
   * the command — not that the node came back up.
   */
  requestReboot: async (nodeId: string): Promise<void> => {
    // A 202 carries no body, and openapi-fetch would run it through
    // `response.json()`; reading it as text keeps an empty body from throwing.
    const { error, response } = await rebootClient.POST(
      '/v1/nodes/{node_id}/reboot',
      {
        params: { path: { node_id: nodeId } },
        parseAs: 'text',
      },
    )

    if (!response.ok) {
      throw new Error(error?.error ?? 'Failed to request a reboot.')
    }
  },
}
